import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FilterOption } from '../../models/filter-option.model';

/** Contador de instancias: da un id unico al listbox para el aria-controls. */
let nextMenuId = 0;

/** Select multiple con chips, equivalente visual al Select2 del original. */
@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  standalone: false,
})
export class MultiSelectComponent implements OnInit {

  @Input() public options: Array<FilterOption> = [];
  @Input() public selected: Array<string> = [];
  @Input() public placeholder = 'TODOS';
  /** false = comportamiento de combo simple (un solo valor). */
  @Input() public multiple = true;

  @Output() public selectedChange = new EventEmitter<Array<string>>();

  public open = false;
  public readonly menuId: string = `z-select-menu-${++nextMenuId}`;

  /** Etiqueta del campo que envuelve al combo (la del <legend> del fieldset). */
  private fieldLabel = '';

  @ViewChild('combo') private comboRef?: ElementRef<HTMLElement>;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  public ngOnInit(): void {
    // El padre la publica como atributo para no ensuciar la API de inputs.
    this.fieldLabel = this.elementRef.nativeElement.getAttribute('data-field-label') ?? '';
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) this.open = false;
  }

  /** Nombre accesible del combobox; el placeholder es el ultimo recurso. */
  public get accessibleName(): string {
    return this.fieldLabel || this.placeholder;
  }

  public get labels(): Array<string> {
    return this.selected
      .map((value) => this.options.find((o) => o.value === value)?.label ?? value);
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR APERTURA                               */
  /* -------------------------------------------------------------------------- */

  public toggleMenu(): void {
    this.open = !this.open;
  }

  public close(): void {
    if (!this.open) return;

    this.open = false;
    this.comboRef?.nativeElement.focus();
  }

  public onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        // Dentro del menu los <button> ya se activan solos con Enter/Espacio.
        if (event.target !== this.comboRef?.nativeElement) return;
        event.preventDefault();
        this.toggleMenu();
        return;

      case 'ArrowDown':
        event.preventDefault();
        if (!this.open) {
          this.open = true;
          setTimeout(() => this.focusOption());
          return;
        }
        this.focusOption();
        return;

      case 'Escape':
        if (!this.open) return;
        event.preventDefault();
        this.close();
        return;

      default:
        return;
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR SELECCION                              */
  /* -------------------------------------------------------------------------- */

  public isSelected(option: FilterOption): boolean {
    return this.selected.includes(option.value);
  }

  public toggle(option: FilterOption): void {
    if (!this.multiple) {
      this.emit([option.value]);
      this.close();
      return;
    }

    const next = this.isSelected(option)
      ? this.selected.filter((v) => v !== option.value)
      : [...this.selected, option.value];

    this.emit(next);
  }

  public remove(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.emit(this.selected.filter((_, i) => i !== index));
    // El chip desaparece con el foco dentro: hay que devolverlo a la caja.
    this.comboRef?.nativeElement.focus();
  }

  public clear(event: MouseEvent): void {
    event.stopPropagation();
    this.emit([]);
    this.comboRef?.nativeElement.focus();
  }

  private emit(values: Array<string>): void {
    this.selected = values;
    this.selectedChange.emit(values);
  }

  private focusOption(): void {
    this.elementRef.nativeElement.querySelector<HTMLButtonElement>('.z-select-menu .opt')?.focus();
  }
}
